<template>
  <v-dialog v-model="dialogOpen" max-width="1240" persistent>
    <v-card class="operation-wizard-card">
      <v-card-title class="operation-wizard-header">
        <div>
          <div class="operation-wizard-title">{{ wizardTitle }}</div>
          <div class="operation-wizard-subtitle">
            {{ wizardSubtitle }}
          </div>
        </div>
        <div class="operation-wizard-header-actions">
          <v-chip v-if="issuePillLabel" outlined color="error">
            {{ issuePillLabel }}
          </v-chip>
          <v-btn outlined color="primary" :disabled="applying" @click="close">
            Kapat
          </v-btn>
        </div>
      </v-card-title>

      <operation-stepper :steps="steps" :current-step="currentStep" />

      <v-card-text class="operation-wizard-content pa-0">
        <div class="operation-wizard-content-inner">
          <v-alert v-if="error" dense outlined type="error" class="mb-4">
            {{ error }}
          </v-alert>

          <operation-scope-overview
            v-if="currentStep === 'scope'"
            :source="source"
            :issue-label="issueLabel"
          />

          <operation-select
            v-else-if="currentStep === 'operation'"
            v-model="selectedOperationType"
            :operations="operationOptions"
            :readonly="isOperationLocked"
            :recommended-operation-type="recommendedOperationType"
          />

          <setup-relation-product-operation-mode
            v-else-if="currentMode === 'setup'"
            :active-step="currentStep"
            :draft="draft"
            :preview="preview"
            @update-draft="updateDraftField"
            @product-selected="handleProductSelected"
            @billing-type-changed="handleBillingTypeChanged"
          />

          <relation-operation-mode
            v-else-if="currentMode === 'relation'"
            :draft="draft"
            :preview="preview"
            @update-draft="updateDraftField"
          />

          <product-operation-mode
            v-else-if="currentMode === 'product'"
            :draft="draft"
            :preview="preview"
            @update-draft="updateDraftField"
            @product-selected="handleProductSelected"
            @billing-type-changed="handleBillingTypeChanged"
          />

          <overlap-relation-operation-mode
            v-else-if="currentMode === 'overlap_relation'"
            :draft="draft"
            :preview="preview"
            @update-draft="updateDraftField"
          />

          <financial-identity-operation-mode
            v-else-if="currentMode === 'financial_identity'"
            :draft="draft"
            :preview="preview"
            :operation-type="resolvedOperationType"
            @update-draft="updateDraftField"
          />

          <authorized-user-operation-mode
            v-else-if="currentMode === 'authorized_user'"
            :draft="draft"
            :preview="preview"
            @update-draft="updateDraftField"
          />

          <v-alert
            v-else-if="currentStep === 'details'"
            dense
            outlined
            type="warning"
          >
            Bu operasyon tipi için mode component henüz bağlanmadı:
            <strong>{{ resolvedOperationType || "-" }}</strong>
          </v-alert>

          <operation-preview-panel
            v-else-if="currentStep === 'preview'"
            :preview="previewForDisplay"
          />

          <operation-result-panel
            v-else-if="currentStep === 'result'"
            :result="result"
          />
        </div>
      </v-card-text>

      <v-card-actions class="operation-wizard-footer">
        <div class="operation-wizard-footer-note">{{ footerNote }}</div>
        <v-spacer />
        <v-btn
          text
          :disabled="isSecondaryActionDisabled"
          @click="handleSecondaryAction"
        >
          {{ secondaryActionLabel }}
        </v-btn>
        <v-btn
          color="primary"
          :loading="loading || applying"
          :disabled="!canContinue"
          @click="goToNextStep"
        >
          {{ primaryActionLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  getDefaultWizardSteps,
  getOperationDefinition,
  getIssueTypeLabel,
  getOperationTypeForIssue,
  getWizardOperationOptions,
  getWizardFlowDefinition,
} from "./operationRegistry";
import { applyBillingOperation, previewBillingOperation } from "./api";
import AuthorizedUserOperationMode from "./modes/AuthorizedUserOperationMode.vue";
import FinancialIdentityOperationMode from "./modes/FinancialIdentityOperationMode.vue";
import OperationPreviewPanel from "./shared/OperationPreviewPanel.vue";
import OperationResultPanel from "./shared/OperationResultPanel.vue";
import OperationScopeOverview from "./shared/OperationScopeOverview.vue";
import OperationSelect from "./shared/OperationSelect.vue";
import OperationStepper from "./shared/OperationStepper.vue";
import OverlapRelationOperationMode from "./modes/OverlapRelationOperationMode.vue";
import ProductOperationMode from "./modes/ProductOperationMode.vue";
import RelationOperationMode from "./modes/RelationOperationMode.vue";
import SetupRelationProductOperationMode from "./modes/SetupRelationProductOperationMode.vue";

export default {
  name: "BillingOperationWizard",
  components: {
    AuthorizedUserOperationMode,
    FinancialIdentityOperationMode,
    OperationPreviewPanel,
    OperationResultPanel,
    OperationScopeOverview,
    OperationSelect,
    OperationStepper,
    OverlapRelationOperationMode,
    ProductOperationMode,
    RelationOperationMode,
    SetupRelationProductOperationMode,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    source: {
      type: Object,
      default: null,
    },
    operationType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      currentStep: "scope",
      selectedOperationType: null,
      draft: this.getEmptyDraft(),
      selectedProduct: null,
      preview: null,
      result: null,
      loading: false,
      applying: false,
      error: null,
    };
  },
  computed: {
    steps() {
      if (
        this.currentFlow &&
        this.currentFlow.steps.some((step) => step.key === this.currentStep)
      ) {
        return this.currentFlow.steps;
      }

      return getDefaultWizardSteps();
    },
    dialogOpen: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    issueType() {
      return this.getSourceValue([
        "issue_type",
        "issueType",
        "riskType",
        "risk_type",
      ]);
    },
    resolvedOperationType() {
      if (this.operationType) {
        return this.operationType;
      }

      if (this.selectedOperationType) {
        return this.selectedOperationType;
      }

      return null;
    },
    recommendedOperationType() {
      return this.issueType ? getOperationTypeForIssue(this.issueType) : null;
    },
    operationDefinition() {
      return getOperationDefinition(this.resolvedOperationType);
    },
    currentFlow() {
      return getWizardFlowDefinition(this.resolvedOperationType);
    },
    flowContext() {
      return {
        step: this.currentStep,
        preview: this.previewForDisplay,
        source: this.source,
      };
    },
    currentMode() {
      return this.currentFlow?.modeByStep?.[this.currentStep] || null;
    },
    operationOptions() {
      return getWizardOperationOptions();
    },
    operationLabel() {
      return this.operationDefinition
        ? this.operationDefinition.label
        : this.resolvedOperationType || "-";
    },
    issuePillLabel() {
      return (
        getIssueTypeLabel(this.issueType) ||
        this.currentFlow?.issuePillLabel ||
        this.issueType ||
        null
      );
    },
    wizardTitle() {
      return (
        this.currentFlow?.title?.(this.flowContext) ||
        "Faturalama Operasyon Aracı"
      );
    },
    wizardSubtitle() {
      return (
        this.currentFlow?.subtitle?.(this.flowContext) ||
        "Kapsam analizi, operasyon seçimi, policy önizlemesi ve uygulama sonucunu tek akışta yönetir."
      );
    },
    isIssueEntry() {
      return Boolean(
        this.source &&
          (this.source.type === "portfolio_issue" ||
            this.source.type === "risk")
      );
    },
    isOperationLocked() {
      return Boolean(this.operationType || this.isIssueEntry);
    },
    issueLabel() {
      return this.issueType || "-";
    },
    currentStepIndex() {
      return this.steps.findIndex((step) => step.key === this.currentStep);
    },
    isFirstStep() {
      return this.currentStepIndex <= 0;
    },
    isLastStep() {
      return this.currentStepIndex === this.steps.length - 1;
    },
    hasSupportedMode() {
      return Boolean(this.currentFlow);
    },
    canContinue() {
      if (this.loading || this.applying) {
        return false;
      }

      if (this.currentStep === "operation") {
        return Boolean(this.resolvedOperationType);
      }

      if (this.currentStep === "details") {
        return this.hasSupportedMode && this.isCurrentOperationDraftReady;
      }

      if (this.currentMode) {
        return this.hasSupportedMode && this.isCurrentOperationDraftReady;
      }

      if (this.currentStep === "preview") {
        return this.canApplyPreview;
      }

      if (this.currentStep === "result") {
        return false;
      }

      return true;
    },
    isSetupDraftReady() {
      return (
        Boolean(this.draft.relation.starts_on) &&
        Boolean(this.draft.product.product_id) &&
        Boolean(this.draft.product.starts_on) &&
        this.draft.product.amount !== null &&
        this.draft.product.amount !== "" &&
        this.draft.product.amount !== undefined
      );
    },
    isProductDraftReady() {
      return (
        Boolean(this.draft.product.product_id) &&
        Boolean(this.draft.product.starts_on) &&
        this.draft.product.amount !== null &&
        this.draft.product.amount !== "" &&
        this.draft.product.amount !== undefined
      );
    },
    isRelationDraftReady() {
      return Boolean(this.draft.relation.starts_on);
    },
    isRelationSelectionDraftReady() {
      return Boolean(this.draft.relation_selection.active_customer_cluster_id);
    },
    isFinancialIdentityDraftReady() {
      return Boolean(
        String(this.draft.financial_identity.tax_number || "").trim()
      );
    },
    isBillingProfileDraftReady() {
      return (
        Boolean(String(this.draft.billing_profile.tax_office || "").trim()) &&
        Boolean(String(this.draft.billing_profile.address || "").trim()) &&
        Boolean(this.draft.billing_profile.city_id) &&
        Boolean(this.draft.billing_profile.district_id)
      );
    },
    isAuthorizedUserDraftReady() {
      return Boolean(this.draft.authorized_user.authorized_user_id);
    },
    isCurrentOperationDraftReady() {
      const readiness = this.currentFlow?.draftReadyByStep?.[this.currentStep];

      if (readiness === "setup") {
        return this.isSetupDraftReady;
      }

      if (readiness === "product") {
        return this.isProductDraftReady;
      }

      if (readiness === "relation") {
        return this.isRelationDraftReady;
      }

      if (readiness === "relation_selection") {
        return this.isRelationSelectionDraftReady;
      }

      if (readiness === "financial_identity") {
        return this.isFinancialIdentityDraftReady;
      }

      if (readiness === "billing_profile") {
        return this.isBillingProfileDraftReady;
      }

      if (readiness === "authorized_user") {
        return this.isAuthorizedUserDraftReady;
      }

      return false;
    },
    selectedResolutionOption() {
      if (this.resolvedOperationType !== "relation.resolve_overlap") {
        return null;
      }

      const selectedRelationId =
        this.draft.relation_selection.active_customer_cluster_id ||
        this.preview?.default_selected_relation_id;

      if (!selectedRelationId) {
        return null;
      }

      return (
        this.preview?.resolution_options?.find(
          (option) => option.selected_relation_id === selectedRelationId
        ) || null
      );
    },
    previewForDisplay() {
      if (this.resolvedOperationType !== "relation.resolve_overlap") {
        return this.preview;
      }

      if (!this.preview || !this.selectedResolutionOption) {
        return this.preview;
      }

      return {
        ...this.preview,
        resolution_plan: {
          can_apply: this.selectedResolutionOption.can_apply,
          blocked_reasons: this.selectedResolutionOption.blocked_reasons || [],
          actions: this.selectedResolutionOption.actions || [],
        },
      };
    },
    canApplyPreview() {
      return Boolean(this.previewForDisplay?.resolution_plan?.can_apply);
    },
    primaryActionLabel() {
      if (this.currentStep === "details") {
        return "Önizle";
      }

      return (
        this.currentFlow?.primaryActionLabels?.[this.currentStep] || "Devam"
      );
    },
    secondaryActionLabel() {
      if (this.isFirstStep) {
        return "Vazgeç";
      }

      return "Geri";
    },
    isSecondaryActionDisabled() {
      return Boolean(this.loading || this.applying);
    },
    footerNote() {
      return (
        this.currentFlow?.footerNotes?.[this.currentStep] ||
        "Operasyon akışı önce önizleme ile çalışır."
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.resetFlow();
        }
      },
    },
    operationType() {
      if (this.value) {
        this.resetFlow();
      }
    },
    source() {
      if (this.value) {
        this.resetFlow();
      }
    },
  },
  methods: {
    getEmptyDraft() {
      return {
        relation: {
          starts_on: null,
          ends_on: null,
        },
        relation_selection: {
          active_customer_cluster_id: undefined,
        },
        financial_identity: {
          tax_number: undefined,
        },
        billing_profile: {
          tax_office: undefined,
          address: undefined,
          city_id: undefined,
          district_id: undefined,
        },
        authorized_user: {
          authorized_user_id: undefined,
        },
        product: {
          product_id: null,
          starts_on: null,
          ends_on: null,
          amount: null,
          is_yearly: false,
        },
      };
    },
    resetFlow() {
      this.selectedOperationType =
        this.operationType || this.recommendedOperationType || null;
      this.currentStep = this.getInitialStepForOperation(
        this.selectedOperationType
      );
      this.draft = this.getEmptyDraft();
      this.selectedProduct = null;
      this.preview = null;
      this.result = null;
      this.loading = false;
      this.applying = false;
      this.error = null;

      if (this.currentMode) {
        this.$nextTick(() => {
          this.fetchPreview({ hydrateDraft: true }).catch((error) => {
            this.handleOperationError(
              error,
              "Billing operasyon önizlemesi alınamadı."
            );
          });
        });
      }
    },
    getInitialStepForOperation(operationType) {
      return getWizardFlowDefinition(operationType)?.initialStep || "scope";
    },
    close() {
      if (this.applying) {
        return;
      }

      this.dialogOpen = false;
    },
    goToPreviousStep() {
      if (this.isFirstStep || this.loading || this.applying) {
        return;
      }

      this.currentStep = this.steps[this.currentStepIndex - 1].key;
    },
    handleSecondaryAction() {
      if (this.isFirstStep) {
        this.close();
        return;
      }

      this.goToPreviousStep();
    },
    async goToNextStep() {
      if (!this.canContinue) {
        return;
      }

      try {
        if (this.currentStep === "operation") {
          if (!this.hasSupportedMode) {
            this.error = "Bu operasyon tipi için UI mode henüz bağlı değil.";
            return;
          }

          await this.fetchPreview({ hydrateDraft: true });
          this.currentStep = this.getInitialStepForOperation(
            this.resolvedOperationType
          );
          return;
        }

        if (this.currentMode) {
          await this.fetchPreview({ hydrateDraft: false });
          this.currentStep =
            this.currentFlow?.nextStepByStep?.[this.currentStep] || "preview";
          return;
        }

        if (this.currentStep === "preview") {
          await this.applyOperation();
          this.currentStep = "result";
          this.$emit("applied", this.result);
          return;
        }

        if (this.isLastStep) {
          return;
        }

        this.currentStep = this.steps[this.currentStepIndex + 1].key;
      } catch (error) {
        this.handleOperationError(error, "Billing operasyonu tamamlanamadı.");
      }
    },
    async fetchPreview({ hydrateDraft }) {
      this.loading = true;
      this.error = null;

      try {
        const preview = await previewBillingOperation(this.buildPayload());
        this.preview = preview;

        if (hydrateDraft) {
          this.hydrateDraftFromPreview(preview, true);
        }

        return preview;
      } finally {
        this.loading = false;
      }
    },
    async applyOperation() {
      this.applying = true;
      this.error = null;

      try {
        this.result = await applyBillingOperation(this.buildPayload());
        return this.result;
      } finally {
        this.applying = false;
      }
    },
    buildPayload() {
      return {
        operation_type: this.resolvedOperationType,
        source: this.buildSourcePayload(),
        input: this.draft,
      };
    },
    buildSourcePayload() {
      return {
        type: this.getSourceValue(["type"]) || "manual",
        issue_type: this.issueType,
        customer_id: this.getSourceValue(["customer_id", "customerId"]),
        cluster_id: this.getSourceValue(["cluster_id", "clusterId"]),
        relation_id: this.getSourceValue(["relation_id", "relationId"]),
      };
    },
    getSourceValue(keys) {
      if (!this.source) {
        return null;
      }

      return keys
        .map((key) => this.source[key])
        .find((value) => value !== undefined && value !== null);
    },
    hydrateDraftFromPreview(preview, force = false) {
      const form = preview?.form || {};

      this.hydrateDraftField(
        "relation",
        "starts_on",
        form.relation_starts_on || form.starts_on,
        force
      );
      this.hydrateDraftField(
        "relation",
        "ends_on",
        form.relation_ends_on || form.ends_on,
        force
      );
      this.hydrateDraftField(
        "relation_selection",
        "active_customer_cluster_id",
        form.active_customer_cluster_id ||
          preview?.default_selected_relation_id,
        force
      );
      this.hydrateDraftField("product", "product_id", form.product_id, force);
      this.hydrateDraftField(
        "product",
        "starts_on",
        form.product_starts_on || form.starts_on,
        force
      );
      this.hydrateDraftField(
        "product",
        "ends_on",
        form.product_ends_on || form.ends_on,
        force
      );
      this.hydrateDraftField("product", "amount", form.amount, force);
      this.hydrateDraftField(
        "financial_identity",
        "tax_number",
        form.tax_number,
        force
      );
      this.hydrateDraftField(
        "billing_profile",
        "tax_office",
        form.tax_office,
        force
      );
      this.hydrateDraftField("billing_profile", "address", form.address, force);
      this.hydrateDraftField("billing_profile", "city_id", form.city_id, force);
      this.hydrateDraftField(
        "billing_profile",
        "district_id",
        form.district_id,
        force
      );
      this.hydrateDraftField(
        "authorized_user",
        "authorized_user_id",
        form.authorized_user_id,
        force
      );

      if (force || this.draft.product.is_yearly === null) {
        this.$set(this.draft.product, "is_yearly", Boolean(form.is_yearly));
      }
    },
    hydrateDraftField(namespace, field, value, force) {
      if (value === undefined) {
        return;
      }

      const currentValue = this.draft[namespace]?.[field];

      if (force || currentValue === null || currentValue === "") {
        this.updateDraftField({ namespace, field, value });
      }
    },
    updateDraftField({ namespace, field, value }) {
      if (!this.draft[namespace]) {
        this.$set(this.draft, namespace, {});
      }

      this.$set(this.draft[namespace], field, value);
    },
    handleProductSelected(product) {
      this.selectedProduct = product || null;

      if (product?.id) {
        this.updateDraftField({
          namespace: "product",
          field: "product_id",
          value: product.id,
        });
      }

      this.updateProductAmountFromSelectedProduct();
    },
    handleBillingTypeChanged() {
      this.updateProductAmountFromSelectedProduct();
    },
    updateProductAmountFromSelectedProduct() {
      if (!this.selectedProduct) {
        return;
      }

      const amount = this.draft.product.is_yearly
        ? this.selectedProduct.yearly_price_per_house
        : this.selectedProduct.monthly_price_per_house;

      this.updateDraftField({
        namespace: "product",
        field: "amount",
        value: amount,
      });
    },
    handleOperationError(error, fallbackMessage) {
      const validationErrors = error?.response?.data?.errors || {};
      const firstValidationMessage = Object.values(validationErrors)
        .reduce((messages, value) => messages.concat(value), [])
        .find(Boolean);
      const message =
        firstValidationMessage ||
        error?.response?.data?.message ||
        fallbackMessage;

      this.error = message;

      if (this.$toast) {
        this.$toast.error(message);
      }
    },
  },
};
</script>

<style scoped>
.operation-wizard-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.operation-wizard-header {
  align-items: flex-start;
  border-bottom: 1px solid #e0e0e0;
  flex: 0 0 auto;
  justify-content: space-between;
  padding: 24px 24px 16px;
}

.operation-wizard-card.v-card > .operation-wizard-header.v-card__title {
  padding: 24px 24px 16px;
}

.operation-wizard-title {
  color: #212121;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.35;
}

.operation-wizard-subtitle {
  color: #8d8d8d;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  margin-top: 8px;
}

.operation-wizard-header-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 12px;
}

.operation-wizard-content {
  flex: 1 1 auto;
  overflow-y: auto;
}

.operation-wizard-content-inner {
  padding: 32px 24px;
}

.operation-wizard-footer {
  border-top: 1px solid #e0e0e0;
  flex: 0 0 auto;
  padding: 16px 24px;
}

.operation-wizard-card.v-card > .operation-wizard-footer.v-card__actions {
  padding: 16px 24px;
}

.operation-wizard-footer-note {
  color: #8d8d8d;
  font-size: 12px;
  line-height: 1.4;
}
</style>
